import { useState } from 'react';
import { Control, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Checkbox, Label, Input, Tabs, TabsList, TabsTrigger } from '@artstorefronts/ui';

interface DimensionsInputProps {
  control: Control<any>;
  widthRegister: any;
  heightRegister: any;
  dimensionTypeRegister: any;
  widthError?: string;
  heightError?: string;
  originalWidth?: number;
  originalHeight?: number;
  onWidthChange?: (value: number | undefined | null) => void;
  onHeightChange?: (value: number | undefined | null) => void;
}

/**
 * Height and width keep exactly what the artist types. Linking them to the
 * PHOTO's pixel ratio is an opt-in ("Keep photo proportions"), OFF by default:
 * a photo is almost never the exact ratio of the canvas, so a lock that was
 * always on made a real size like 18 × 24 in impossible to enter (member
 * reports 2026-08-25 and 2026-09-15, issue 3451). The physical size of the work
 * is a fact about the work, not the photo. Locked by
 * `e2e/tests/artwork-dimensions-exact.spec.ts`.
 */
function DimensionsInput({
  control,
  widthRegister,
  heightRegister,
  widthError,
  heightError,
  originalWidth,
  originalHeight,
  onWidthChange,
  onHeightChange,
}: DimensionsInputProps) {
  const { t } = useTranslation('artwork');
  const [keepProportions, setKeepProportions] = useState(false);
  const photoRatio = originalWidth && originalHeight ? originalWidth / originalHeight : null;
  const aspectRatio = keepProportions ? photoRatio : null;

  const handleHeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const heightValue = e.target.value;
    heightRegister.onChange(e);

    if (!heightValue) {
      if (aspectRatio && onWidthChange) {
        onWidthChange(undefined);
      }
    } else if (heightValue && aspectRatio && onWidthChange) {
      const numericHeight = Number(heightValue);
      if (!isNaN(numericHeight) && numericHeight > 0) {
        const calculatedWidth = numericHeight * aspectRatio;
        onWidthChange(Number(calculatedWidth.toFixed(2)));
      }
    }
  };

  const handleWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const widthValue = e.target.value;
    widthRegister.onChange(e);

    if (!widthValue) {
      if (aspectRatio && onHeightChange) {
        onHeightChange(undefined);
      }
    } else if (widthValue && aspectRatio && onHeightChange) {
      const numericWidth = Number(widthValue);
      if (!isNaN(numericWidth) && numericWidth > 0) {
        const calculatedHeight = numericWidth / aspectRatio;
        onHeightChange(Number(calculatedHeight.toFixed(2)));
      }
    }
  };

  return (
    <div className="mb-4">
      <div className="mt-2 grid grid-cols-3 gap-2">
        <div>
          <Label htmlFor="height" className="text-sm font-bold">
            {t('dimensions.height')}
          </Label>
          <Input
            {...heightRegister}
            id="height"
            placeholder={t('dimensions.placeholder.height')}
            className="mt-1"
            onChange={handleHeightChange}
          />
        </div>
        <div>
          <Label htmlFor="width" className="text-sm font-bold">
            {t('dimensions.width')}
          </Label>
          <Input
            {...widthRegister}
            id="width"
            placeholder={t('dimensions.placeholder.width')}
            className="mt-1"
            onChange={handleWidthChange}
          />
        </div>
        <div className="mt-1">
          <Label htmlFor="dimensionType" className="text-sm">
            &nbsp;
          </Label>
          <Controller
            name="dimensionType"
            control={control}
            render={({ field }) => (
              <Tabs
                value={field.value}
                onValueChange={(value) => {
                  if (value) {
                    field.onChange(value as 'inches' | 'centimeters');
                  }
                }}
              >
                <TabsList className="grid w-28 grid-cols-2 bg-transparent">
                  <TabsTrigger value="inches">{t('dimensions.unit.inches-abbr')}</TabsTrigger>
                  <TabsTrigger value="centimeters">{t('dimensions.unit.centimeters-abbr')}</TabsTrigger>
                </TabsList>
              </Tabs>
            )}
          />
        </div>
      </div>
      {photoRatio && (
        <label htmlFor="keep-proportions" className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-gray-600">
          <Checkbox
            id="keep-proportions"
            className="h-4 w-4 shrink-0"
            checked={keepProportions}
            onCheckedChange={(checked) => setKeepProportions(checked === true)}
          />
          <span>{t('dimensions.keep-proportions')}</span>
        </label>
      )}
      {heightError && <p className="text-sm text-red-500 mt-1">{heightError}</p>}
      {widthError && <p className="text-sm text-red-500 mt-1">{widthError}</p>}
    </div>
  );
}

export default DimensionsInput;
